import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputLabel, Link, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField, Typography, useMediaQuery } from '@mui/material'
import { CancelOutlined, ChevronLeft, ChevronRight } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import LocationService from '../../../../shared/services/location/location.service';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { ILocation } from '../../../../shared/services/location/location.model';
import { IReportPeriod, IArchivedAndReportPeriodDetails } from '../../../../shared/services/report-period/report-period.model';
import gaiaTheme from '../../../../root-components/theme';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import { IGeneratedSecrReportResponse, IReportSECRGenerate } from '../../../../shared/services/generated-report/generated-report.model';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import ArchivedReportPeriodService from '../../../../shared/services/archived-report-period/archived-report-period.service';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import IntensityRatioService from '../../../../shared/services/intensity-ratio/intensity-ratio.service';
import { IIntensityRatio } from '../../../../shared/services/intensity-ratio/intensity-ratio.model';
import { ImportStepHeader } from '../../../pages/settings/data-import/import-data/steps/import-step-header';
import { BRAND_DISPLAY_FONT } from '../../../pages/signup-login/auth/auth-branding';

// The two named steps of the guided flow (same shape as the CRP wizard, smaller).
const STEPS = ['Report options', 'Narratives and sign-off']

const STEP_DESCRIPTIONS = [
  'Choose what the report covers and how it compares across years. The standard settings suit most SECR reports.',
  'Add the statutory narrative disclosures and an optional sign-off, then generate the report.',
]

export const ReportingSecrForm: FunctionComponent<{
  submitHandler: (values: IReportSECRGenerate) => void,
  isEditPage?: boolean,
  report?: IGeneratedSecrReportResponse,
  isSubmitting: boolean,
}> = ({
  submitHandler,
  isEditPage = false,
  isSubmitting,
  report }) => {

    interface IComparisonYears {
      id: string,
      friendlyName: string,
      manualEntry: boolean,
      baseYear: boolean,
    }

    const navigate = useNavigate()
    const reportPeriodContext = useContext(ReportPeriodContext);
    const locationService = LocationService.getInstance();
    const archivedReportPeriodService = new ArchivedReportPeriodService();
    const intensityRatioService = new IntensityRatioService();
    const reportPeriodService = new ReportPeriodService();
    const [searchParams, setSearchParams] = useSearchParams()
    const [locations, setLocations] = useState<ILocation[]>()
    const [updatedLocations, setUpdatedLocations] = useState<ILocation[]>()
    const [comparisonYears, setComparisonYears] = useState<IComparisonYears[]>()
    const [formattedArchivedReportPeriods, setFormattedArchivedReportPeriods] = useState<IComparisonYears[]>()
    const [selectedComparisonYearsId, setSelectedComparisonYearsId] = useState<string[]>([])
    const [selectedIntensityRatio, setSelectedIntensityRatio] = useState<string[]>([])
    const [intensityRatioOptions, setIntensityRatioOptions] = useState<IIntensityRatio[]>([])
    const [openSignOffDate, setOpenSignOffDate] = useState<boolean>(false)
    // Which of the two steps is showing. All values live in the one formik instance (plus the two
    // multi-select states above), so moving between steps never loses anything.
    const [activeStep, setActiveStep] = useState<number>(0)
    // Set after "Use standard SECR settings" so the caption can say what was chosen. The ratio
    // key is kept so the summary can be cleared if the user later changes anything the preset set.
    const [preset, setPreset] = useState<{ summary: string, ratioKey: string | null } | null>(null)
    // Fresh, resolved period figures (revenue + employees) fetched from the backend so the
    // read-only display reflects edits made on the reporting period without a full reload
    // (ReportPeriodContext is not refreshed on those edits).
    const [periodDetails, setPeriodDetails] = useState<IArchivedAndReportPeriodDetails>()
    // Dead code: the "change number of employees" dialog was removed when revenue/employees
    // became read-only (sourced from the reporting period). Kept commented for now.
    // const [openDialog, setDialogOpen] = React.useState(false);
    const [reportPeriod, setReportPeriod] = useState<IReportPeriod>()
    const [totalNumberOfEmployees, setTotalNumberOfEmployees] = useState<number>(0)
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

    useEffect(() => {
      if (searchParams.has('reportPeriodId')) {
        const token = searchParams.get('reportPeriodId')
        if (token) {
          const allReportPeriods = reportPeriodContext.allReportPeriods
          if (allReportPeriods) {
            const foundReportPeriod = allReportPeriods.find(reportPeriod => reportPeriod.id === token)
            if (foundReportPeriod) {
              setReportPeriod(foundReportPeriod)
            }
          }
        }
      }
    }, [reportPeriodContext.allReportPeriods])

    useEffect(() => {
      const current = formik.values.totalRevenue
      const isEmpty = current === null || current === undefined || current === ''
      const reportPeriodHasRevenue = reportPeriod && reportPeriod.totalRevenue != null
      if (reportPeriodHasRevenue && isEmpty) {
        formik.setFieldValue('totalRevenue', reportPeriod.totalRevenue)
      }
    }, [reportPeriod])

    useEffect(() => {
      const current = formik.values.totalRevenue
      const isEmpty = current === null || current === undefined || current === ''
      const reportHasRevenue = report && report.totalRevenue != null
      if (reportHasRevenue && isEmpty) {
        formik.setFieldValue('totalRevenue', report.totalRevenue)
      }
    }, [report])

    useEffect(() => {
      if (reportPeriod) {
        const formattedReportPeriod: IComparisonYears[] = reportPeriodContext.lockedReportPeriods.filter(report => report.id !== reportPeriod.id).map(r => ({
          id: r.id,
          friendlyName: r.friendlyName,
          manualEntry: false,
          baseYear: r.baseYear,
        }))
        if (formattedArchivedReportPeriods) {
          setComparisonYears([...formattedReportPeriod, ...formattedArchivedReportPeriods])
        }
      }
      if (report) {
        const foundReport = report.reportDetails.find(r => r.isPrimaryReportPeriod)
        if (foundReport) {
          const formattedReportPeriod: IComparisonYears[] = reportPeriodContext.lockedReportPeriods.filter(reportPeriod => reportPeriod.id !== foundReport.reportPeriodId).map(r => ({
            id: r.id,
            friendlyName: r.friendlyName,
            manualEntry: false,
            baseYear: r.baseYear,
          }))

          if (formattedArchivedReportPeriods) {
            setComparisonYears([...formattedReportPeriod, ...formattedArchivedReportPeriods])
          }
        }
      }
    }, [formattedArchivedReportPeriods, reportPeriodContext.lockedReportPeriods, reportPeriod])


    useEffect(() => {
      getArchivedReportPeriods();
    }, [])

    // The steps are long enough to scroll, so a step change would otherwise leave the user
    // part-way down the page with the heading off screen.
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [activeStep])

    const getArchivedReportPeriods = () => {
      archivedReportPeriodService.getAllArchivedReportPeriods()
        .then(response => {
          const formattedData: IComparisonYears[] = response.map(r => ({
            id: r.id,
            friendlyName: r.friendlyName,
            manualEntry: true,
            baseYear: r.baseYear,
          }))
          setFormattedArchivedReportPeriods(formattedData)
        })
        .catch(err => console.log(err))
    }

    const secrSchema = Yup.object().shape({
      comparisonYearsIds: Yup.array().of(Yup.string()),
      // SECR must report at least one intensity ratio, so an empty selection blocks Generate.
      intensityRatios: Yup.array().of(Yup.string()).min(1, 'Select at least one intensity ratio: SECR reports are required to include one'),
      // includeBaseYear: Yup.boolean(),
      includeGlobal: Yup.boolean(),
      includeScopeThree: Yup.boolean(),
      includeEnergyCategoryBreakdown: Yup.boolean(),
      includeDataQuality: Yup.boolean(),
      includeAssumptions: Yup.boolean(),
      includeAcknowledgedFindings: Yup.boolean(),
      includeReductionDisclosures: Yup.boolean(),
      numberOfUkEmployees: Yup.number().integer('Must be a whole number').min(0).nullable(),
      numberOfNonUkEmployees: Yup.number().integer('Must be a whole number').min(0).nullable(),
      // Revenue is sourced read-only from the reporting period, so it is not required here.
      totalRevenue: Yup.number().nullable().notRequired(),
      // §S2 (TICKET-H3): statutory SECR narrative disclosures.
      methodologyStatement: Yup.string().nullable(),
      energyEfficiencyNarrative: Yup.string().nullable(),
      // Print-only sign-off capture; all optional.
      signOffName: Yup.string().nullable(),
      signOffTitle: Yup.string().nullable(),
      signOffDate: Yup.string().nullable(),
    })

    const formik = useFormik({
      initialValues: {
        reportPeriodId: '',
        comparisonYearsIds: [],
        intensityRatios: [],
        // includeBaseYear: report ? report.includeBaseYear : false,
        includeGlobal: report ? report.includeGlobal : false,
        includeScopeThree: report ? report.includeScopeThree : false,
        includeEnergyCategoryBreakdown: report ? report.includeEnergyCategoryBreakdown !== false : true,
        includeDataQuality: report ? report.includeDataQuality !== false : true,
        includeAssumptions: report ? report.includeAssumptions !== false : true,
        includeAcknowledgedFindings: report ? report.includeAcknowledgedFindings !== false : true,
        includeReductionDisclosures: report ? report.includeReductionDisclosures !== false : true,
        numberOfUkEmployees: null,
        numberOfNonUkEmployees: null,
        locationEmployees: [],
        totalRevenue: null,
        methodologyStatement: report?.methodologyStatement ?? '',
        energyEfficiencyNarrative: report?.energyEfficiencyNarrative ?? '',
        signOffName: report?.signOffName ?? '',
        signOffTitle: report?.signOffTitle ?? '',
        signOffDate: report?.signOffDate ?? '',
      },
      validationSchema: secrSchema,
      onSubmit: (values) => {
        if (!isEditPage && reportPeriod) {
          values.reportPeriodId = reportPeriod.id
        }
        if (isEditPage && report) {
          const reportPeriodId = report.reportDetails.find(report => report.isPrimaryReportPeriod)?.reportPeriodId
          if (reportPeriodId) {
            values.reportPeriodId = reportPeriodId
          }
        }
        // Formik initialises the sign-off fields to '', and '' in a Postgres date column is
        // an error, not an empty value (the CRP sanitise rule). Normalise on the way out.
        submitHandler({
          ...values,
          signOffName: values.signOffName || null,
          signOffTitle: values.signOffTitle || null,
          signOffDate: values.signOffDate || null,
        })
      }
    });

    useEffect(() => {
      intensityRatioService.getEnabled()
        .then(ratios => {
          setIntensityRatioOptions(ratios)
          // SECR requires at least one intensity ratio, so default to the organisation's
          // first enabled ratio (display order) rather than starting empty. Edit mode keeps
          // the report's saved selection, and scope 3 ratios are not defaulted because the
          // scope 3 toggle starts off.
          if (!report?.intensityRatios?.length) {
            const defaultRatio = ratios.find(ratio => !ratio.includesScopeThree)
            if (defaultRatio) {
              setSelectedIntensityRatio(prev => prev.length > 0 ? prev : [defaultRatio.key])
            }
          }
        })
        .catch(err => console.log(err))
    }, [])

    const primaryPeriodId = reportPeriod?.id
      ?? report?.reportDetails.find(d => d.isPrimaryReportPeriod)?.reportPeriodId

    useEffect(() => {
      if (!primaryPeriodId) return
      reportPeriodService.getReportPeriodorArchivedReportPeriod(primaryPeriodId)
        .then(setPeriodDetails)
        .catch(err => console.log(err))
    }, [primaryPeriodId])

    useEffect(() => {
      locationService.getAllLocations()
        .then(response => {
          setLocations(response)
          setUpdatedLocations(response)
          setTotalNumberOfEmployees((response.map(location => location.numberOfEmployees).reduce((a, c) => a + c, 0)))
          // Prefer the reporting period's stored UK/Non-UK employee counts when set;
          // otherwise fall back to deriving them from the org's locations.
          if (reportPeriod && reportPeriod.numberOfUkEmployees != null) {
            formik.setFieldValue('numberOfUkEmployees', reportPeriod.numberOfUkEmployees || null);
            formik.setFieldValue('numberOfNonUkEmployees', reportPeriod.numberOfNonUkEmployees || null);
          } else if ((response.map(location => location.numberOfEmployees).reduce((a, c) => a + c, 0)) > 0) {
            const numberOfNonUkEmployees = response
              .filter(location => location.countryId !== 'GBR')
              .map(loc => loc.numberOfEmployees)
              .reduce((a, c) => a + c, 0);
            const numberOfUkEmployees = response
              .filter(location => location.countryId === 'GBR')
              .map(loc => loc.numberOfEmployees)
              .reduce((a, c) => a + c, 0);
            formik.setFieldValue('numberOfUkEmployees', numberOfUkEmployees || null);
            formik.setFieldValue('numberOfNonUkEmployees', numberOfNonUkEmployees || null);
          }
        })
        .catch(err => {
          console.log(err)
        })
    }, [reportPeriod])

    useEffect(() => {
      if (report && report.intensityRatios.length > 0) {
        setSelectedIntensityRatio(report.intensityRatios)
      }

      if (report && report.reportDetails.length > 1) {
        const notPrimaryReports = report.reportDetails.filter(reportDetail => !reportDetail.isPrimaryReportPeriod)
        setSelectedComparisonYearsId(notPrimaryReports.map(report => report.reportPeriodId))
      }
    }, [])

    useEffect(() => {
      if (formik.values.includeScopeThree === false) {
        const scopeThreeKeys = intensityRatioOptions.filter(o => o.includesScopeThree).map(o => o.key)
        if (selectedIntensityRatio.some(key => scopeThreeKeys.includes(key))) {
          setSelectedIntensityRatio(prev => prev.filter(key => !scopeThreeKeys.includes(key)))
        }
      }
    }, [formik.values.includeScopeThree, intensityRatioOptions])

    useEffect(() => {
      if (comparisonYears && !isEditPage) {
        const baseYearReportPeriod = comparisonYears.find(year => year.baseYear)
        if (baseYearReportPeriod) {
          if (!selectedComparisonYearsId.includes(baseYearReportPeriod.id))
            selectedComparisonYearsId.push(baseYearReportPeriod.id)
        }
      }
    }, [comparisonYears])

    useEffect(() => {
      formik.setFieldValue('comparisonYearsIds', selectedComparisonYearsId);
    }, [selectedComparisonYearsId]);

    useEffect(() => {
      formik.setFieldValue('intensityRatios', selectedIntensityRatio);
    }, [selectedIntensityRatio]);

    const selectComparisonHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      setSelectedComparisonYearsId(typeof value === 'string' ? value.split(',') : value,);
    }

    const selectIntensityHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      setSelectedIntensityRatio(typeof value === 'string' ? value.split(',') : value);
    }

    // Dead code: handlers for the removed "change number of employees" dialog. Revenue and
    // employees are now read-only on the SECR form (sourced from the reporting period), so
    // these are unused. Kept commented for now.
    // const openDialogHandler = () => {
    //   setDialogOpen(true);
    // };

    // const closeDialogHandler = () => {
    //   setDialogOpen(false);
    // };

    // const successHandler = (updatedLocations: ILocation[]) => {
    //   const numberOfNonUkEmployees = updatedLocations
    //     .filter(location => location.countryId !== 'GBR')
    //     .map(loc => loc.numberOfEmployees)
    //     .reduce((a, c) => a + c, 0);
    //   const numberOfUkEmployees = updatedLocations
    //     .filter(location => location.countryId === 'GBR')
    //     .map(loc => loc.numberOfEmployees)
    //     .reduce((a, c) => a + c, 0);
    //   formik.setFieldValue('numberOfNonUkEmployees', numberOfNonUkEmployees || null);
    //   formik.setFieldValue('numberOfUkEmployees', numberOfUkEmployees || null);
    //   setTotalNumberOfEmployees((updatedLocations.map(location => location.numberOfEmployees).reduce((a, c) => a + c)))
    //   const formattedData = updatedLocations.map(location => {
    //     return {
    //       locationId: location.id,
    //       numberOfEmployees: location.numberOfEmployees
    //     }
    //   })
    //   formik.setFieldValue('locationEmployees', formattedData)
    //   closeDialogHandler()
    // }

    const cancelHandler = () => {
      formik.resetForm();
      if (report) {
        navigate(`/reporting/secr/${report.id}`)
      }
    }

    // One-click recommended setup. It turns the four annex sections on, leaves the two
    // scope-widening toggles alone (off unless the user already turned them on), and sets the
    // intensity ratio to the organisation's default (first enabled non scope 3 ratio).
    const applyStandardSettings = () => {
      formik.setFieldValue('includeEnergyCategoryBreakdown', true)
      formik.setFieldValue('includeDataQuality', true)
      formik.setFieldValue('includeAssumptions', true)
      formik.setFieldValue('includeReductionDisclosures', true)
      const defaultRatio = intensityRatioOptions.find(ratio => !ratio.includesScopeThree)
      if (defaultRatio) {
        setSelectedIntensityRatio([defaultRatio.key])
      }
      const scopeNote = (formik.values.includeScopeThree || formik.values.includeGlobal)
        ? 'Scope 3 and global comparison were left as you set them.'
        : 'Scope 3 and global comparison stay off.'
      setPreset({
        summary: `Applied: energy and emissions breakdown, data quality disclosure, assumptions register, and reduction targets and offsets are on${defaultRatio ? `; intensity ratio set to ${defaultRatio.label}` : ''}. ${scopeNote}`,
        ratioKey: defaultRatio?.key ?? null,
      })
    }

    // The summary caption must not keep claiming sections are on that the user has since turned
    // off, so it clears as soon as any field the preset set is changed away from what it chose.
    useEffect(() => {
      if (!preset) return
      const annexSectionsOn = formik.values.includeEnergyCategoryBreakdown
        && formik.values.includeDataQuality
        && formik.values.includeAssumptions
        && formik.values.includeReductionDisclosures
      const ratioUnchanged = preset.ratioKey === null
        || (selectedIntensityRatio.length === 1 && selectedIntensityRatio[0] === preset.ratioKey)
      if (!annexSectionsOn || !ratioUnchanged) {
        setPreset(null)
      }
    }, [formik.values.includeEnergyCategoryBreakdown, formik.values.includeDataQuality, formik.values.includeAssumptions, formik.values.includeReductionDisclosures, selectedIntensityRatio])

    const handleNext = () => {
      // The statutory intensity ratio is the only step 1 field that can block Generate, so
      // surface its error here rather than letting the user find out on step 2.
      if (selectedIntensityRatio.length === 0) {
        formik.setFieldTouched('intensityRatios', true)
        return
      }
      setActiveStep(1)
    }

    const handleBack = () => {
      setActiveStep(0)
    }

    // Leaving the flow means the same thing on both steps: back to the report in edit mode,
    // back to the SECR list otherwise.
    const handleCancel = isEditPage ? cancelHandler : () => navigate('/reporting/secr')

    // Safety net: if the intensity ratio selection is somehow empty at Generate time, return
    // to step 1 with the error showing instead of a silent no-op submit.
    const handleGenerate = () => {
      if (selectedIntensityRatio.length === 0) {
        formik.setFieldTouched('intensityRatios', true)
        setActiveStep(0)
        return
      }
      formik.submitForm()
    }

    const primaryReportPeriodNameFromReport = report
      ? report.reportDetails.find(r => r.isPrimaryReportPeriod)?.reportPeriodName || report.reportDetails[0]?.reportPeriodName
      : undefined;

    const comparisonYearOptions = comparisonYears ?? []

    const selectedRatioDefinitions = intensityRatioOptions.filter(o => selectedIntensityRatio.includes(o.key))
    const showEmployeeInput = selectedRatioDefinitions.some(o => o.denominatorType === 'employees')
    const showRevenueInput = selectedRatioDefinitions.some(o => o.denominatorType === 'revenue')

    const totalRevenueLabel = reportPeriod
      ? `Total Revenue for ${reportPeriod.friendlyName} in £`
      : primaryReportPeriodNameFromReport
        ? `Total Revenue for ${primaryReportPeriodNameFromReport} in £`
        : 'Total Revenue in £';

    // The reporting period is the source of truth for the denominators, so they show read-only
    // here (with a link to edit them on the period). In edit mode the current period is
    // resolved from context via the report's primary period id.
    const effectivePeriod = reportPeriod
      ?? (report ? reportPeriodContext.allReportPeriods?.find(p => p.id === report.reportDetails.find(d => d.isPrimaryReportPeriod)?.reportPeriodId) : undefined);
    const periodEditLink = effectivePeriod
      ? `/settings/company-settings/reporting-periods/${effectivePeriod.id}`
      : '/settings/company-settings/reporting-periods';
    // Prefer the freshly fetched period figures; fall back to the (possibly cached) context
    // values, then to the location-derived total, while the fetch is in flight.
    const displayRevenue = periodDetails?.totalRevenue ?? effectivePeriod?.totalRevenue;
    const displayEmployees = periodDetails
      ? ((periodDetails.numberOfUkEmployees ?? 0) + (periodDetails.numberOfNonUkEmployees ?? 0))
      : (effectivePeriod && effectivePeriod.numberOfUkEmployees != null)
        ? (effectivePeriod.numberOfUkEmployees + (effectivePeriod.numberOfNonUkEmployees ?? 0))
        : totalNumberOfEmployees;

    // One include/exclude row. On desktop the explanation is a tooltip beside the label; on
    // mobile the tooltip button is not rendered, so the same text appears as helper text under
    // the control instead of being dropped.
    const renderIncludeOption = (
      name: string,
      checked: boolean,
      label: string,
      help: string,
      opts?: { disabled?: boolean, indent?: boolean },
    ) => (
      <Box sx={{ mb: 3, ml: opts?.indent ? 4 : 0 }} key={name}>
        <Stack display='flex' flexDirection='row'>
          <FormControlLabel control={
            <Checkbox
              onChange={formik.handleChange}
              checked={checked}
              disabled={opts?.disabled}
            />} label={label} name={name} />
          {!isMobileMode && <TooltipButton
            text={help}
            placement='right'
          />}
        </Stack>
        {isMobileMode && (
          <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', pl: 4 }}>{help}</Typography>
        )}
      </Box>
    )

    const stepOneContent = (
      <Stack display='flex' flexDirection='column'>
        <Box sx={{ mb: 3 }}>
          <Button variant='outlined' onClick={applyStandardSettings} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
            <Typography>Use standard SECR settings</Typography>
          </Button>
          <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            {preset?.summary ?? 'One click sets the recommended report: energy and emissions breakdown, data quality disclosure, assumptions register, and reduction targets and offsets all on; scope 3 and global comparison off unless you have turned them on; your default intensity ratio.'}
          </Typography>
        </Box>
        {reportPeriod && <FormControl disabled sx={{ mb: 3 }}>
          <TextField sx={{ color: 'text.secondary' }} value={reportPeriod.friendlyName} disabled />
        </FormControl>}
        {/* Always rendered: when no locked or archived periods exist yet, the control is
            disabled with an explanation instead of silently disappearing. */}
        <FormControl fullWidth variant='outlined' sx={{ mb: 3 }} disabled={comparisonYearOptions.length === 0}>
          <InputLabel htmlFor='comparisonYearsIds'>Comparison Years</InputLabel>
          <Select id='comparisonYearsIds' name='comparisonYearsIds' label='comparison years'
            value={selectedComparisonYearsId || ''}
            onChange={selectComparisonHandler}
            multiple
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value, index) => {
                  const selectedName = comparisonYearOptions.find(period => period.id === value)?.friendlyName
                  return (
                    <Chip key={index} label={selectedName} />
                  )
                })}
              </Box>
            )}
            input={<OutlinedInput label="Comparison Years" />}
          >
            {comparisonYearOptions.filter(year => !year.baseYear).map((reportPeriod, index) => (
              <MenuItem key={reportPeriod.id} value={reportPeriod.id}>
                <Checkbox checked={selectedComparisonYearsId?.includes(reportPeriod.id)} />
                <ListItemText primary={reportPeriod.friendlyName} />
              </MenuItem>
            ))}
            {comparisonYearOptions.find(year => year.baseYear) &&
              <MenuItem value={comparisonYearOptions.find(year => year.baseYear)?.id} disabled>
                <Checkbox checked />
                <ListItemText primary={comparisonYearOptions.find(year => year.baseYear)?.friendlyName} />
              </MenuItem>}
          </Select>
          {comparisonYearOptions.length > 0
            ? <Typography variant='body2' sx={{ color: 'text.secondary' }}>Statutory: SECR expects a comparison with the previous reporting year. Your base year report period will automatically be included as a comparison</Typography>
            : <Typography variant='body2' sx={{ color: 'text.secondary' }}>Comparison years become available once a previous reporting period is closed</Typography>}
        </FormControl>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2'>Report content</Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            All of these sections are optional under SECR.
          </Typography>
        </Box>
        {renderIncludeOption(
          'includeGlobal',
          formik.values.includeGlobal,
          'Include comparison to global emissions',
          'This will expand the report to include global emissions data in addition to the default UK emissions.',
        )}
        {renderIncludeOption(
          'includeScopeThree',
          formik.values.includeScopeThree,
          'Include scope 3 emissions',
          'This will include Scope 3 emissions in the report. Note that reporting Scope 3 emissions is not compulsory.',
        )}
        {renderIncludeOption(
          'includeEnergyCategoryBreakdown',
          formik.values.includeEnergyCategoryBreakdown,
          'Include energy and emissions breakdown by category (Scope 1 & 2)',
          'Shows kWh and emissions (Scope 1 and Scope 2) split by SECR source (e.g. electricity, gas). Intensity ratios appear only in the summary section above. Archived comparison years only include summary energy totals.',
        )}
        {renderIncludeOption(
          'includeDataQuality',
          formik.values.includeDataQuality,
          'Include data quality disclosure',
          'Adds a supplementary annex table showing how each reported total was calculated (activity data, spend-based estimates, assumed data), following the GHG Protocol method hierarchy.',
        )}
        {renderIncludeOption(
          'includeAssumptions',
          formik.values.includeAssumptions,
          'Include assumptions register',
          'Adds a supplementary annex listing the assumptions recorded against this reporting period, so estimates and judgement calls are visible next to the figures.',
        )}
        {renderIncludeOption(
          'includeAcknowledgedFindings',
          formik.values.includeAcknowledgedFindings,
          'Include acknowledged readiness findings',
          'Lists the readiness findings that were acknowledged for this period in the assumptions register, with the recorded reason and who accepted it. Untick to keep the register to manually recorded assumptions only.',
          { disabled: !formik.values.includeAssumptions, indent: true },
        )}
        {renderIncludeOption(
          'includeReductionDisclosures',
          formik.values.includeReductionDisclosures,
          'Include reduction targets and carbon offsets',
          'Adds a supplementary annex with your reduction targets and progress, plan coverage, and the carbon offsets register. Reported totals stay gross; offsets are never deducted.',
        )}
        <FormControl fullWidth variant='outlined'>
          <InputLabel htmlFor='intensityRatios'>Intensity Ratio *</InputLabel>
          <Select id='intensityRatios' name='intensityRatios' label='intensity ratio *'
            value={selectedIntensityRatio}
            onChange={selectIntensityHandler}
            multiple
            error={formik.touched.intensityRatios && Boolean(formik.errors.intensityRatios)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value, index) => {
                  const selectedName = intensityRatioOptions.find(ratio => ratio.key === value)?.label
                  return (
                    <Chip key={index} label={selectedName} />
                  )
                })}
              </Box>
            )}
            input={<OutlinedInput label="Intensity Ratio *" />}
          >
            {intensityRatioOptions
              ?.filter(ratio => formik.values.includeScopeThree || !ratio.includesScopeThree)
              .map((ratio, index) => (
                <MenuItem key={index} value={ratio.key}>
                  <Checkbox checked={selectedIntensityRatio?.includes(ratio.key)} />
                  <ListItemText primary={ratio.label} />
                </MenuItem>
              ))}
          </Select>
          {formik.touched.intensityRatios && formik.errors.intensityRatios
            ? <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.intensityRatios}</FormHelperText>
            : <Typography variant='body2' sx={{ color: 'text.secondary' }}>Statutory: SECR reports must include at least one intensity ratio</Typography>}
        </FormControl>
        <Stack display='flex' flexDirection='row' gap={2}>
          {showEmployeeInput &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 3 }}>
              <InputLabel htmlFor='numberOfEmployees'>Number of Employees</InputLabel>
              <OutlinedInput id='numberOfEmployees' name='numberOfEmployees' type='number' label='number of employees'
                value={displayEmployees ?? ''}
                inputProps={{ min: 0 }}
                disabled
              ></OutlinedInput>
            </FormControl>
          }
          {showRevenueInput &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 3 }}>
              <InputLabel htmlFor='totalRevenue'>{totalRevenueLabel}</InputLabel>
              <OutlinedInput id='totalRevenue' name='totalRevenue' type='number' label={totalRevenueLabel}
                value={displayRevenue ?? ''}
                startAdornment={<Typography variant='body2' sx={{ color: 'text.secondary', mr: 0.5 }}>£</Typography>}
                disabled
              ></OutlinedInput>
            </FormControl>}
        </Stack>
        {(showEmployeeInput || showRevenueInput) &&
          <Typography variant='caption' sx={{ color: 'text.secondary', mt: 1 }}>
            Revenue and employee figures are taken from the reporting period.{' '}
            <Link component={RouterLink} to={periodEditLink} sx={{ fontFamily: BRAND_DISPLAY_FONT }}>Edit on reporting period</Link>
          </Typography>}
      </Stack>
    )

    const stepTwoContent = (
      <>
        {/* §S2 (TICKET-H3): statutory SECR narrative disclosures. */}
        <Stack spacing={2}>
          <Box>
            <Typography variant='subtitle2'>Narrative disclosures</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Statutory: SECR reports must describe the methodology used and the energy efficiency action taken in the year.
            </Typography>
          </Box>
          <TextField
            id='methodologyStatement'
            name='methodologyStatement'
            label='Methodology statement (SECR)'
            placeholder='e.g. GHG Protocol Corporate Standard; UK Government DEFRA conversion factors (gross CV); operational control boundary.'
            multiline
            minRows={2}
            fullWidth
            value={formik.values.methodologyStatement}
            onChange={formik.handleChange}
          />
          <TextField
            id='energyEfficiencyNarrative'
            name='energyEfficiencyNarrative'
            label='Energy efficiency action narrative (SECR)'
            placeholder='Describe the energy-efficiency measures taken during the reporting period.'
            multiline
            minRows={2}
            fullWidth
            value={formik.values.energyEfficiencyNarrative}
            onChange={formik.handleChange}
          />
        </Stack>
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Box>
            <Typography variant='subtitle2'>Report sign-off</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Optional. Appears on the downloaded PDF only, not on this page. To change it later, regenerate the report.
            </Typography>
          </Box>
          <TextField
            id='signOffName'
            name='signOffName'
            label='Signed on behalf of the company by'
            placeholder='e.g. the director or board member approving the report'
            fullWidth
            value={formik.values.signOffName}
            onChange={formik.handleChange}
          />
          <TextField
            id='signOffTitle'
            name='signOffTitle'
            label='Job title'
            fullWidth
            value={formik.values.signOffTitle}
            onChange={formik.handleChange}
          />
          <FormControl fullWidth variant='outlined'>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label='Sign-off date'
                value={formik.values.signOffDate ? moment(formik.values.signOffDate) : null}
                onChange={(date) => formik.setFieldValue('signOffDate', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                format='DD/MM/YYYY'
                open={openSignOffDate}
                onOpen={() => setOpenSignOffDate(true)}
                onClose={() => setOpenSignOffDate(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpenSignOffDate(true),
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </Stack>
      </>
    )

    return (
      <Box component='form'>
        <ImportStepHeader currentStep={activeStep + 1} totalSteps={STEPS.length} />
        <Typography variant='h6' sx={{ mb: 1 }}>{STEPS[activeStep]}</Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>{STEP_DESCRIPTIONS[activeStep]}</Typography>
        {activeStep === 0 ? stepOneContent : stepTwoContent}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Button
            variant='outlined'
            onClick={handleBack}
            startIcon={<ChevronLeft />}
            disabled={activeStep === 0 || isSubmitting}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            <Typography>Back</Typography>
          </Button>
          {activeStep === 0 &&
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end'>
              <Button variant='text' onClick={handleCancel} sx={{ mr: 1 }}>
                <CancelOutlined />
                <Typography sx={{ p: 0.5 }}>Cancel</Typography>
              </Button>
              <Button
                variant='contained'
                onClick={handleNext}
                endIcon={<ChevronRight />}
                sx={{ minWidth: 100, textTransform: 'none' }}
              >
                <Typography>Next</Typography>
              </Button>
            </Stack>}
          {activeStep === 1 &&
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end'>
              <DialogSaveButtons
                isForm={true}
                isSubmitting={isSubmitting}
                cancelHandler={handleCancel}
                submitHandler={handleGenerate}
                buttonInitialText='Generate Report'
                buttonLoadingText='Generating Report'
              />
            </Stack>}
        </Box>
      </Box>
    )
  }
